import moment from 'moment';

import { ChangeEvent } from 'react';

// eslint-disable-next-line
export const emailValidator = /^\w+([\.-]?\w+){2,}@\w+([\.-]?\w+)+(\.\w{2,})+$/;
export const numberValidator = /^[\d]+$/;
export const passwordValidator =
	/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,120}$/;

export const VALID_VERSION_FORMAT_REGEX = /^\d{1,2}\.\d{1,2}\.\d{1,3}$/;
export const ONLY_ENGLISH_LETTERS_REGEX = /^[A-Za-z0-9 ]+$/g;
export const ONLY_ARABIC_LETTERS_REGEX = /^[\u0621-\u064A\u0660-\u0669 ]+$/g;

export const atLeastOneLetterValidator = /[a-zA-Z]/;

export const hexColor = (color: any, theme: any) => {
	return theme.palette[color].dark;
};

export const hexToRGB = (colorHex: any, alpha?: any) => {
	const r = parseInt(colorHex.slice(1, 3), 16),
		g = parseInt(colorHex.slice(3, 5), 16),
		b = parseInt(colorHex.slice(5, 7), 16);

	if (alpha) {
		return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
	} else {
		return 'rgb(' + r + ', ' + g + ', ' + b + ')';
	}
};

export const checkObjEqual = (
	obj1: Record<string, any>,
	obj2: Record<string, any>,
) => {
	return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const convertToBase64 = async (file: File) => {
	return new Promise((resolve, reject) => {
		if (file?.type) {
			const fileReader = new FileReader();
			fileReader.readAsDataURL(file);
			fileReader.onload = () => {
				resolve(fileReader.result?.toString()?.split(',').pop());
			};
			fileReader.onerror = error => {
				reject(error);
			};
		} else {
			reject('empty_file');
		}
	});
};

export const bytesToSize = (bytes: number) => {
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	if (bytes === 0) return '0 Byte';
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
};

export const handleNumberInputMinMaxChange = (
	e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	min: number,
	max: number,
	handleChange: (
		e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
		parsedValue: number,
	) => any,
) => {
	const parsed = Number(e.target.value);
	if (!isNaN(parsed) && parsed >= min && parsed <= max) {
		e.target.value = parsed.toString();
		handleChange(e, parsed);
	}
};

export const handleDateInputChange = (
	e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	setter: (value?: Date) => any,
) => {
	if (e.target.value) {
		const formattedValue = new Date(e.target.value);
		setter(formattedValue);
		return;
	}
	setter(undefined);
};

export const formateDateToStringDate = (
	date?: Date,
	onlyDate = false,
): string => {
	if (date && Boolean(date)) {
		const correctedDate = new Date(
			date.getTime() - date.getTimezoneOffset() * 60000,
		)
			?.toJSON()
			?.slice(0, 16);

		if (onlyDate) {
			return correctedDate.slice(0, 10);
		}
		return correctedDate;
	}
	return '';
};

export const formateDateToStringDateOnly = (date?: Date): string => {
	if (date && Boolean(date)) {
		date.setHours(0);
		date.setSeconds(0);
		date.setMinutes(0);
		date.setMilliseconds(0);
		return date.toISOString().slice(0, 16);
	}
	return '';
};

export const getDateAfterDays = (days: number) => {
	const date = new Date();
	date.setDate(date.getDate() + days);

	return date;
};

export const generateRangeArray = (from: number, to: number): number[] => {
	if (to <= from) return [];
	const rangeArray: number[] = [];

	for (let i = from; i <= to; i++) {
		rangeArray.push(i);
	}

	return rangeArray;
};

export const getWeekOfMonth = (date: Date): number => {
	let adjustedDate = date.getDate() + date.getDay();
	let prefixes = ['0', '1', '2', '3', '4', '5'];
	return parseInt(prefixes[0 | (adjustedDate / 7)]) + 1;
};

export const generateDatesRangeArray = (
	from: Date,
	to: Date,
	unitOfTime: 'day' | 'week' | 'month' | 'year',
	format: string,
): string[] => {
	if (from === to) {
		return [moment(from).format(format)];
	}
	const rangeArray: string[] = [];
	const fromDate = moment(from).startOf(unitOfTime);
	const toDate = moment(to).endOf(unitOfTime);

	while (fromDate.isBefore(toDate)) {
		rangeArray.push(fromDate.format(format));
		fromDate.add(1, unitOfTime);
	}

	return rangeArray;
};

export const deleteEmptyObjectProperties = (
	obj: Record<string, any>,
	valuesToDelete: any[] = [],
	exceptKeys: string[] = [],
) => {
	for (const key in obj) {
		if (exceptKeys.includes(key)) continue;
		const value = obj[key];
		if (
			value === '' ||
			value === undefined ||
			value === null ||
			valuesToDelete.includes(value)
		)
			delete obj[key];
	}

	return obj;
};

export const transformValueOrUseDefault = <T>(
	value: string | undefined | null,
	transformObject: { [key: string]: T },
	_default: T,
): T => {
	if (value === undefined || value === null) return _default;
	if (transformObject[value] !== undefined && transformObject[value] !== null)
		return transformObject[value];
	return _default;
};

export const formateApiDateValue = (date?: string) => {
	return date ? date.slice(0, -1) : '';
};

export const formateNullableEnumValue = <T extends number>(value: T) => {
	if (value || value === 0) return value;
	return -1;
};

export const getObjectValueIfExists = <T, k extends Record<string, any>>(
	obj: k,
	key: keyof k,
	undefinedValue?: T,
) => {
	if (obj[key] !== undefinedValue) {
		return { [key]: obj[key] };
	}
	return {};
};

export const getObjectValuesIfExists = <k extends Record<string, any>>(
	obj: k,
	key: keyof k,
	undefinedValues: any[] = [undefined],
) => {
	if (!undefinedValues.includes(obj[key])) {
		return { [key]: obj[key] };
	}
	return {};
};

export const validateImageResolution = async (
	file: File | null,
	exactWidth: number,
	exactHeight: number,
): Promise<boolean> => {
	if (!file) return false;

	return new Promise<boolean>(resolve => {
		const img = new Image();
		img.onload = function () {
			if (img.width === exactWidth && img.height === exactHeight) {
				resolve(true); // Image resolution is valid
			} else {
				resolve(false); // Image resolution is not valid
			}
		};
		img.onerror = function () {
			resolve(false); // Error loading the image
		};
		img.src = URL.createObjectURL(file);
	});
};

export const playAudio = async (src: string): Promise<void> => {
	return new Promise((resolve, reject) => {
		const audio = new Audio(src);

		audio.addEventListener('ended', () => {
			resolve();
		});

		audio.addEventListener('error', err => {
			reject(err);
		});

		audio.play();
	});
};

export const playAudiosInSequence = async (
	audiosSources: string[],
): Promise<void> => {
	for (const audiosSource of audiosSources) {
		await playAudio(audiosSource);
	}
};

export const extractAllArrayCombinationsByLength = (
	arr: number[],
	length?: number,
): number[][] => {
	length = length || arr.length - 1;
	const result: number[][] = [];

	const generateCombination = (current: number[], start: number) => {
		if (current.length === length) {
			result.push([...current]);
			return;
		}

		for (let i = start; i < arr.length; i++) {
			current.push(arr[i]);
			generateCombination(current, i + 1);
			current.pop();
		}
	};

	generateCombination([], 0);

	return result;
};
